import { ApiClient } from "../ApiClient";

/**
 * Create a New Subscription
 * @param {Object} data - Payload the plan type
 * @returns {Promise<Response>}
 */
export const createSubscription = async (data) => {
  return ApiClient.post("/subscriptions/subscriptions", data);
};

/**
 * Fetch a paginated history of the user's subscriptions
 * @returns {Promise<Response>} The raw fetch response
 */
export const getSubscriptions = async () => {
  return ApiClient.get("/subscriptions/subscriptions");
};

/**
 * Fetch public pricing plan configurations for a specific feature by its slug
 * @param {string} slug - The slug identifier for the subscription feature (e.g., 'financial-insights')
 * @returns {Promise<Response>} The raw fetch response
 */
export const getSubscriptionFeatureDetails = async (slug) => {
  // ApiClient.get appends your baseUrl, auth headers, and returns the raw Response zone cleanly
  return ApiClient.get(`/subscriptions/subscription-feature/${slug}`);
};

/**
 * Initialize a Stripe Setup Intent for Recurring Subscription Plans
 * @param {Object} data - Payload containing the subscription_id UUID
 * @returns {Promise<Response>}
 */
export const getSubscriptionSetupIntent = async (data) => {
  return ApiClient.post("/payments/subscription-setup-intent", data);
};

/**
 * Initialize a Stripe Payment Intent for One-Time Tax Filings
 * @param {Object} data - Payload containing the filingUuid
 * @returns {Promise<Response>}
 */
export const getFilingPaymentIntent = async (data) => {
  return ApiClient.post("/payments/payment-intent-filing", data);
};

/**
 * Fetch the current details and status of a specific user subscription
 * @param {string} subscriptionUuid - The UUID of the subscription (purchaseId)
 * @param {boolean} isCheckout - Set to true to receive pending upgrade details
 * @returns {Promise<Response>} The raw fetch response containing the subscription status
 */
export const getSubscriptionDetails = async (
  subscriptionUuid,
  isCheckout = false,
) => {
  const url = isCheckout
    ? `/subscriptions/${subscriptionUuid}?is_checkout=true`
    : `/subscriptions/${subscriptionUuid}`;

  return ApiClient.get(url);
};

/**
 * Fetch the Setup Intent status for a specific financial insights subscription
 * @param {string|number} subscriptionUuid - The UUID/ID of the user subscription
 * @returns {Promise<Response>} The raw fetch response
 */
export const getSubscriptionSetupIntentStatus = async (subscriptionUuid) => {
  return ApiClient.get(
    `/payments/subscription-setup-intent/${subscriptionUuid}`,
  );
};

/**
 * Update a user subscription's status with Stripe integration on the backend
 * @param {string} subscriptionUuid - The UUID of the subscription
 * @param {Object} data - Payload containing the target status configuration
 * @returns {Promise<Response>} The raw fetch response
 */
export const updateSubscriptionStatus = async (subscriptionUuid, data) => {
  return ApiClient.put(`/subscriptions/${subscriptionUuid}`, data);
};

/**
 * @param {string} subscriptionUuid - The unique identifier (UUID) of the subscription in your local database.
 * @param {Object} payload - The request body defining the action.
 * @param {('upgrade'|'downgrade'|'unsubscribe'|'renew')} payload.action - The targeted subscription operation.
 * @param {string} [payload.plan_code] - El slug identifier for the plan (Required for upgrades/downgrades). E.g., 'fi_paid_basic'.
 * @param {('MONTHLY'|'QUARTERLY'|'YEARLY')} [payload.interval] - The translated Stripe billing cycle enum (Required for upgrades/downgrades).
 * @returns {Promise<Response>} A promise resolving to the native Fetch API Response object.
 */
export const updateSubscriptionDetails = async (subscriptionUuid, payload) => {
  return ApiClient.put(`/subscriptions/${subscriptionUuid}`, payload);
};
